/**
 * TanStack Query hooks for the user's own invites — budget, list, mint,
 * revoke. Mirrors the API in packages/api/src/routes/invites.routes.ts
 * for the `/me/*` endpoints.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/shared/api/client';

export interface MyInvite {
  id: string;
  code: string;
  status: 'pending' | 'redeemed' | 'revoked';
  invitedEmail: string | null;
  createdAt: string;
  usedAt: string | null;
  rewardType?: string | null;
}

interface BudgetResponse {
  budget: number;
}

interface InvitesListResponse {
  invites: MyInvite[];
}

interface GenerateResponse {
  invite: MyInvite & { emailSent: boolean; expiresAt: string | null };
}

export const myInvitesKeys = {
  all: ['f', 'me', 'invites'] as const,
  list: () => [...myInvitesKeys.all, 'list'] as const,
  budget: () => [...myInvitesKeys.all, 'budget'] as const,
};

/** Read current invite budget (number remaining). */
export function useInviteBudget() {
  return useQuery<number>({
    queryKey: myInvitesKeys.budget(),
    queryFn: async () => {
      const data = await api.get<BudgetResponse>('/me/invite-budget');
      return data.budget;
    },
    staleTime: 30_000,
  });
}

/** Read the user's own invite history. */
export function useMyInvites() {
  return useQuery<MyInvite[]>({
    queryKey: myInvitesKeys.list(),
    queryFn: async () => {
      const data = await api.get<InvitesListResponse>('/me/invites');
      return data.invites;
    },
    staleTime: 15_000,
  });
}

/**
 * Mint a new invite. Optional `invitedEmail` causes the API to also
 * send the templated invite email. Decrements budget atomically server-
 * side; the budget query is invalidated on success so the UI refreshes.
 */
export function useGenerateInvite() {
  const qc = useQueryClient();
  return useMutation<GenerateResponse['invite'], Error, { invitedEmail?: string }>({
    mutationFn: (input) =>
      api.post<GenerateResponse>('/me/invites/generate', input).then((r) => r.invite),
    onSuccess: (invite) => {
      qc.invalidateQueries({ queryKey: myInvitesKeys.list() });
      qc.invalidateQueries({ queryKey: myInvitesKeys.budget() });
      if (invite.invitedEmail) {
        toast.success(
          invite.emailSent
            ? `Invite sent to ${invite.invitedEmail}`
            : `Invite created — email send failed, share the link directly`
        );
      } else {
        toast.success('Invite link created');
      }
    },
    onError: (err) => {
      // 403 budget-exhausted comes back with a clear message — preserve it.
      const msg = err.message?.includes('used all of your invites')
        ? err.message
        : (err.message ?? 'Could not create invite');
      toast.error(msg);
    },
  });
}

/** Revoke one of the user's own pending invites. Refunds the budget. */
export function useRevokeMyInvite() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (inviteId) => api.post(`/me/invites/${inviteId}/revoke`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: myInvitesKeys.list() });
      qc.invalidateQueries({ queryKey: myInvitesKeys.budget() });
      toast.success('Invite revoked — your budget was refunded');
    },
    onError: () => {
      toast.error('Could not revoke invite');
    },
  });
}
